import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Box, Paper, Typography, Skeleton, TableContainer,
  Table, TableHead, TableRow, TableCell, TableBody, Alert,
  FormGroup, FormControlLabel, Checkbox, TableSortLabel
} from '@mui/material';
import { logEvent } from '../analytics';
import { SEASONS } from '../seasons';

const PlayerStats = () => {
    const [playerStats, setPlayerStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [visibleSeasons, setVisibleSeasons] = useState({});
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('totalGoals');

    useEffect(() => {
        const fetchAllPlayerData = async () => {
            document.title = "Jets '14 | Player Stats";
            try {
                // Initialize season visibility
                const initialSeasonVisibility = SEASONS.reduce((acc, season) => {
                    acc[season.label.substring(0, 5)] = true;
                    return acc;
                }, {});
                setVisibleSeasons(initialSeasonVisibility);

                // Fetch all games from all seasons via unified-games
                const headers = { 'Accept': 'application/json, text/plain, */*' };
                const seasonPromises = SEASONS.map(async (season) => {
                    const url = `https://gamesheetstats.com/api/unified-games/${season.seasonId}?division=${season.divisionId}`;
                    const res = await fetch(url, { headers });
                    if (!res.ok) throw new Error(`HTTP error fetching games for ${season.label}! status: ${res.status}`);
                    const json = await res.json();
                    return {
                        label: season.label.substring(0, 5),
                        games: json.data || []
                    };
                });

                const allSeasonsData = await Promise.all(seasonPromises);

                const playersMap = new Map();

                allSeasonsData.forEach(({ label, games }) => {
                    games.forEach(g => {
                        ['home', 'visitor'].forEach(side => {
                            const teamTitle = g[side]?.title || '';
                            const goalDetails = g[side]?.goalDetails || [];

                            goalDetails.forEach(gd => {
                                const pId = String(gd.id || '');
                                if (!pId) return;

                                const first = (gd.firstName || '').trim();
                                const last = (gd.lastName || '').trim();
                                const name = `${first} ${last}`.trim().toLowerCase().replace(/\b\w/g, l => l.toUpperCase());

                                if (!playersMap.has(pId)) {
                                    playersMap.set(pId, {
                                        id: pId,
                                        name: name || `Player ${pId}`,
                                        teams: new Set(),
                                        stats: {},
                                        totalGoals: 0
                                    });
                                }

                                const player = playersMap.get(pId);
                                if (teamTitle) player.teams.add(teamTitle);
                                if (!player.stats[label]) {
                                    player.stats[label] = { goals: 0, team: teamTitle };
                                }
                                player.stats[label].goals += 1;
                                player.totalGoals += 1;
                            });
                        });
                    });
                });

                const processedStats = Array.from(playersMap.values()).map(p => ({
                    id: p.id,
                    name: p.name,
                    teams: Array.from(p.teams).join(', '),
                    stats: p.stats,
                    totalGoals: p.totalGoals
                }));

                setPlayerStats(processedStats);

            } catch (e) {
                setError(e.message);
                console.error("Failed to fetch player stats:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchAllPlayerData();
    }, []);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);
        logEvent('sort_player_stats', { sort_by: property, sort_order: newOrder });
    };

    const handleSeasonFilterChange = (event) => {
        setVisibleSeasons({
            ...visibleSeasons,
            [event.target.name]: event.target.checked,
        });
        logEvent('filter_season_player_stats', { season_label: event.target.name, visible: event.target.checked });
    };
    
    const visibleSeasonLabels = Object.entries(visibleSeasons).filter(([, isVisible]) => isVisible).map(([label]) => label);

    const sortedPlayerStats = useMemo(() => {
        const comparator = (a, b) => {
            let valA, valB;
            if (orderBy === 'name') {
                valA = a.name.toLowerCase();
                valB = b.name.toLowerCase();
            } else if (orderBy === 'teams') {
                valA = a.teams.toLowerCase();
                valB = b.teams.toLowerCase();
            } else if (orderBy === 'totalGoals') {
                valA = a.totalGoals;
                valB = b.totalGoals;
            } else {
                // Handle season-specific stats like '23/24-goals'
                const [seasonLabel] = orderBy.split('-');
                valA = a.stats[seasonLabel]?.goals ?? -1;
                valB = b.stats[seasonLabel]?.goals ?? -1;
            }

            if (valB < valA) {
                return order === 'asc' ? 1 : -1;
            }
            if (valB > valA) {
                return order === 'asc' ? -1 : 1;
            }
            return 0;
        };
        return [...playerStats].sort(comparator);
    }, [playerStats, order, orderBy]);

    return (
        <Container maxWidth="lg">
            <Box sx={{ my: 4 }}>
                <Paper variant="outlined" elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
                    <Typography variant="h4" component="h1" gutterBottom textAlign="center">
                        Player Statistics (Goals Scored)
                    </Typography>
                    
                    {error && <Alert severity="error">Could not load player stats: {error}</Alert>}
                    
                    {!loading && (
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6">Filter Seasons</Typography>
                            <FormGroup sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                                {SEASONS.map(season => (
                                    <FormControlLabel
                                        key={season.key}
                                        control={
                                            <Checkbox
                                                checked={visibleSeasons[season.label.substring(0, 5)] || false}
                                                onChange={handleSeasonFilterChange}
                                                name={season.label.substring(0, 5)}
                                            />
                                        }
                                        label={season.label}
                                    />
                                ))}
                            </FormGroup>
                        </Box>
                    )}

                    <TableContainer>
                        <Table sx={{ minWidth: 650 }} aria-label="player stats table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'name'}
                                            direction={orderBy === 'name' ? order : 'asc'}
                                            onClick={() => handleRequestSort('name')}
                                        >
                                            Player Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'teams'}
                                            direction={orderBy === 'teams' ? order : 'asc'}
                                            onClick={() => handleRequestSort('teams')}
                                        >
                                            Teams
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={orderBy === 'totalGoals'}
                                            direction={orderBy === 'totalGoals' ? order : 'asc'}
                                            onClick={() => handleRequestSort('totalGoals')}
                                        >
                                            Total Goals
                                        </TableSortLabel>
                                    </TableCell>
                                    {visibleSeasonLabels.map(label => (
                                        <TableCell key={label} align="right">
                                            <TableSortLabel
                                                active={orderBy === `${label}-goals`}
                                                direction={orderBy === `${label}-goals` ? order : 'asc'}
                                                onClick={() => handleRequestSort(`${label}-goals`)}
                                            >
                                                {label} Goals
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from(new Array(10)).map((_, index) => (
                                        <TableRow key={index}>
                                            <TableCell colSpan={3 + visibleSeasonLabels.length}><Skeleton /></TableCell>
                                        </TableRow>
                                    ))
                                ) : sortedPlayerStats.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3 + visibleSeasonLabels.length} align="center">
                                            No player goal data available.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedPlayerStats.map((player) => (
                                        <TableRow key={player.id}>
                                            <TableCell sx={{ fontWeight: 'medium' }}>{player.name}</TableCell>
                                            <TableCell>{player.teams}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{player.totalGoals}</TableCell>
                                            {visibleSeasonLabels.map(label => (
                                                <TableCell key={label} align="right">
                                                    {player.stats[label]?.goals ?? '-'}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Container>
    );
};

export default PlayerStats;